import React, { useEffect, useState } from 'react'
import { Autocomplete, Box, Button, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import Loader from 'shared/components/atoms/Loader'
import { API } from 'aws-amplify'
import { Toast } from '../../toast/toast'

function generateSecurePassword(length: number = 12): string {
  const charGroups = [
    "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
    "abcdefghijklmnopqrstuvwxyz",
    "0123456789",
    "!@#$%^&*()_+[]{}|:,.<>?"
  ]

  const ensuredChars = charGroups.map(
    (group) => group[Math.floor(Math.random() * group.length)]
  )

  const allChars = charGroups.join("")
  const remainingChars = Array.from(
    { length: length - ensuredChars.length },
    () => allChars[Math.floor(Math.random() * allChars.length)]
  )

  return [...ensuredChars, ...remainingChars]
    .sort(() => Math.random() - 0.5)
    .join("")
}

const UserForm: React.FC = () => {
  const { handleSubmit } = useForm()
  const navigate = useNavigate()
  const [formState, setFormState] = useState({
    username: '',
    email: '',
    group: 'User',
    tempPassword: generateSecurePassword(),
  })
  const [emailError, setEmailError] = useState<string>('')
  const [copied, setCopied] = useState<boolean>(false)
  const [cognitoGroups, setCognitoGroups] = useState<string[]>([])
  const [loading, setLoading] = useState({ groups: true, submitting: false })

  useEffect(() => {
    (
      async () => {
        try {
          const response = await API.get("CorpOnboarding", "/cognito_groups", {})
          setCognitoGroups(response)
        } catch (error) {
          console.error("Error fetching Cognito groups:", error)
          Toast.show({
            type: 'error',
            text1: 'Failed to fetch groups. Please try again.',
          })
        } finally {
          setLoading((prev) => ({ ...prev, groups: false }))
        }
      }
    )()
  }, [])

  const onSubmit = async () => {
    setLoading((prev) => ({ ...prev, submitting: true }))
    try {
      await API.post("CorpOnboarding", "/cognito_user", {
        body: {
          attributes: {
            ...formState,
            temporary_password: formState.tempPassword,
          },
        },
        timeout: 120000,
      })
      Toast.show({
        type: 'success',
        text1: 'User was successfully created.',
      })
      navigate('/users-management/manage')
    } catch (error) {
      console.error("Error saving Cognito User:", error)
      Toast.show({
        type: 'error',
        text1: 'Failed to create user. Please try again.',
      })
    } finally {
      setLoading((prev) => ({ ...prev, submitting: false }))
    }
  }

  const handleFieldChange = (field: string, value: string) => {
    setFormState((prev) => ({ ...prev, [field]: value }))
    if (field === 'email') setEmailError('')
  }

  const handleEmailBlur = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    setEmailError(emailRegex.test(formState.email) ? '' : 'Invalid email address')
  }

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(formState.tempPassword)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={5}>
        <Box>
          <Typography variant="h2" sx={{ marginBottom: 3 }}>Username</Typography>
          <TextField
            name="username"
            placeholder="Username"
            required
            fullWidth
            value={formState.username}
            onChange={(e) => handleFieldChange('username', e.target.value)}
          />
        </Box>
        <Box>
          <Typography variant="h2" sx={{ marginBottom: 3 }}>Email</Typography>
          <TextField
            name="email"
            placeholder="Email"
            value={formState.email}
            onChange={(e) => handleFieldChange('email', e.target.value)}
            onBlur={handleEmailBlur}
            required
            fullWidth
            error={!!emailError}
            helperText={emailError}
          />
        </Box>
        <Box>
          <Typography variant="h2" sx={{ marginBottom: 3 }}>Group</Typography>
          <Loader loading={loading.groups}>
            <Autocomplete
              options={cognitoGroups}
              clearOnEscape
              blurOnSelect
              autoHighlight
              value={formState.group}
              onChange={(e, newValue) => handleFieldChange('group', newValue || 'User')}
              renderInput={(params) => <TextField {...params} required placeholder="Group" />}
            />
          </Loader>
        </Box>
        <Box>
          <Typography variant="h2" sx={{ marginBottom: 3 }}>
            Temporary Password
            <Tooltip title={copied ? "Copied!" : "Copy to Clipboard"}>
              <IconButton onClick={handleCopyToClipboard} sx={{ marginLeft: 1 }}>
                {copied ? <CheckIcon color="success" /> : <ContentCopyIcon />}
              </IconButton>
            </Tooltip>
          </Typography>
          <TextField
            name="password"
            placeholder="Password"
            value={formState.tempPassword}
            required
            fullWidth
            disabled
            helperText="This is an auto-generated temporary password to be shared with the user for first time login to change password"
          />
        </Box>
        <Box>
          <Button
            variant="contained"
            type="submit"
            disabled={loading.submitting || loading.groups}
            sx={{
              background: '#426BF0',
              marginTop: 8,
              width: '15%',
              '&.Mui-disabled': {
                background: '#426BF0',
                color: '#ffffff',
              },
            }}
          >
            {loading.submitting
              ? 'Creating User...'
              : loading.groups
              ? 'Loading...'
              : 'Create User'}
          </Button>
        </Box>
      </Stack>
    </form>
  )
}

export default UserForm
